import React, { useState } from 'react';
import { useAppContext, ACTIONS } from '../context/AppContext';
import { getCallStyling } from '../utils/styleUtils';
import { formatPhoneNumber } from '../utils/format';

const CALL_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'appointment', label: 'Appointments' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'billing', label: 'Billing' },
  { value: 'faq', label: 'FAQ' },
];

const CallQueueView = () => {
  const { state, dispatch } = useAppContext();
  const [selectedType, setSelectedType] = useState('all');

  const handleInteractionSelect = (interaction) => {
    dispatch({ type: ACTIONS.SELECT_INTERACTION, payload: interaction });
    
    // If the interaction is unread, dispatch an action to mark it as read (unread: false)
    if (interaction.unread) {
      dispatch({
        type: ACTIONS.UPDATE_INTERACTION_UNREAD_STATUS,
        payload: {
          interactionId: interaction.id,
          status: false
        }
      });
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const interactionTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - interactionTime) / (1000 * 60));
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const allInteractionsForSelectedType = state.interactions.filter(interaction => {
    return selectedType === 'all' || interaction.type === selectedType;
  });

  const pendingInteractionsForSelectedType = allInteractionsForSelectedType
    .filter(interaction => interaction.status === 'pending')
    .sort((a, b) => {
      const aStyling = getCallStyling(a, state.interactionTypes);
      const bStyling = getCallStyling(b, state.interactionTypes);
      if (aStyling.urgency !== bStyling.urgency) {
        return aStyling.urgency - bStyling.urgency;
      }
      return new Date(b.timestamp) - new Date(a.timestamp);
    });

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="flex-shrink-0">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
            <h2 className="text-lg font-semibold text-gray-800">Call Queue</h2>
            <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full">
              {pendingInteractionsForSelectedType.length}
            </span>
          </div>
          <div>
            <select
              className="text-sm border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-300"
              value={selectedType}
              onChange={e => setSelectedType(e.target.value)}
            >
              {CALL_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Interaction List - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        {pendingInteractionsForSelectedType.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <div className="text-4xl mb-2">📞</div>
            <p className="text-sm">No pending calls</p>
            <p className="text-xs text-gray-400">All caught up!</p>
          </div>
        ) : (
          <div className="p-2 space-y-2 pb-4">
            {pendingInteractionsForSelectedType.map((interaction) => {
              const { label, pill, dot } = getCallStyling(interaction, state.interactionTypes);

              return (
                <div
                  key={interaction.id}
                  onClick={() => handleInteractionSelect(interaction)}
                  className="bg-white rounded-lg p-3 cursor-pointer hover:bg-gray-50 transition-colors duration-200 border border-gray-200 hover:border-primary-300 hover:shadow-sm"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div>
                        {interaction.patientName ? (
                          <>
                            <h3 className="font-semibold text-gray-900 text-base">
                              {interaction.patientName}
                            </h3>
                            <p className="text-sm text-gray-500">{formatPhoneNumber(interaction.patientPhone)}</p>
                          </>
                        ) : (
                          <h3 className="font-semibold text-gray-900 text-base">
                            {formatPhoneNumber(interaction.patientPhone)}
                          </h3>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {interaction.unread && <div className={`w-2 h-2 rounded-full ${dot}`}></div>}
                      <span className={`text-xs px-2 py-1 rounded-full border ${pill}`}>
                        {label}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">
                    {interaction.request}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">
                      {formatTimeAgo(interaction.timestamp)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-gray-200 bg-gray-50 flex-shrink-0">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>Today's calls: {allInteractionsForSelectedType.length}</span>
          <span>Pending: {pendingInteractionsForSelectedType.length}</span>
        </div>
      </div>
    </div>
  );
};

export default CallQueueView; 